/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Component } from '@appCanvasCommon/component-system/Component';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ViewportChangedEvent } from '@appCanvasCommon/interfaces/camera.interface';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
/**
 * CloseOnInteraction
 * This is intended to be a "catchall" for closing elements when the user performs an action that results in a camera
 * update event to dispatch. This is intended to run with the `PlanDrag`, `PlanPinch` and `PlanTap` and not a replacement
 * Essentially you use this when you intend to close modal windows or overlay UI.
 */
export class CloseOnInteraction extends Component implements ComponentInterface {
	/**
	 * Shutdown observable to cleanup any lingering watchers. This is dispatched as part of the `destroy()` canvas lifecycle
	 * @private
	 */
	private _shutdownSubject$: Subject<void> = new Subject<void>();
	/**
	 * The setup method is called once all properties on a component have been decorated by the ComponentManager.
	 * From this point on, you can safely use this.getParent() and this.getManager().
	 * Arguments passed into setup are passed in via the ComponentManager.add(name, component, ...args)
	 */
	public override setup(canvasAngularLand: CanvasAngularLand): void {
		this.canvasAngularLand = canvasAngularLand;
		// Watching the camera here because we want to close all of the _things_ when there is an "interaction"
		// outside of the particular component in Angularland, then make the things close. Historically this was done
		// on the `PlanTap`, `PlanDrag` and `PlanPinch` components. However, there are actions where we want elements to cleanup
		// that are not tied to an event, like a mouse scroll wheel (`wheel` event).
		// We could just watch the `wheel` event, however there are user actions that trigger a scale change that do NOT dispatch
		// a wheel event, like the "zoom buttons" or the "constraint panel", which would cause components we want to close to remain
		// So...consider this a "catch all" for everything we want to close when the user does something that causes the camera to change.
		canvasAngularLand.cameraService.camera.changed$
			.pipe(takeUntil(this._shutdownSubject$))
			.subscribe((event: ViewportChangedEvent) => {
				canvasAngularLand.timelineTicketService.closeOverflow();
			});
	}
	/**
	 * Our parent `Component` class does not have a shutdown subject, so we override destroy here to cleanup our own
	 * subject in this derived class.
	 * @param options
	 */
	public override destroy(options?: IDestroyOptions) {
		super.destroy(options);
		this._shutdownSubject$.next();
		this._shutdownSubject$.complete();
	}
}
